import React, { useState, useEffect } from 'react';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import CreateUserForm from './CreateUserForm';
import UserManagement from './UserManagement';
import SyncManagement from './SyncManagement';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user, logout, hasRole } = useAuth();
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [showUserManagement, setShowUserManagement] = useState(false);
  const [showSyncManagement, setShowSyncManagement] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Закрытие мобильного меню при изменении размера экрана
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) { // md breakpoint
        setMobileMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const getRoleDisplayName = (role: string) => {
    const roleNames = {
      admin: 'Администратор',
      moderator: 'Модератор',
      user: 'Пользователь',
      guest: 'Гость'
    };
    return roleNames[role as keyof typeof roleNames] || role;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <h1 className="text-xl font-semibold text-gray-900">
                Просмотр фото ТП
              </h1>
            </div>

            <div className="flex items-center space-x-4">
              {/* Desktop Navigation */}
              <div className="hidden md:flex items-center space-x-4">
                {/* Кнопка синхронизации только для админов */}
                {hasRole(['admin']) && (
                  <button
                    onClick={() => setShowSyncManagement(true)}
                    className="bg-green-600 hover:bg-green-700 text-white px-3 py-2 rounded-md text-sm font-medium transition-colors"
                  >
                    Настройки синхронизации
                  </button>
                )}

                {/* Кнопки управления для админов и модераторов */}
                {hasRole(['admin', 'moderator']) && (
                  <>
                    <button
                      onClick={() => setShowUserManagement(true)}
                      className="bg-gray-600 hover:bg-gray-700 text-white px-3 py-2 rounded-md text-sm font-medium transition-colors"
                    >
                      Управление пользователями
                    </button>
                    <button
                      onClick={() => setShowCreateUser(true)}
                      className="bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-2 rounded-md text-sm font-medium transition-colors"
                    >
                      Создать пользователя
                    </button>
                  </>
                )}

                {/* Информация о пользователе */}
                <div className="flex items-center space-x-3">
                  <div className="text-sm">
                    <div className="font-medium text-gray-900">{user?.username}</div>
                    <div className="text-gray-500">{getRoleDisplayName(user?.role || 'guest')}</div>
                  </div>

                  <button
                    onClick={logout}
                    className="text-gray-500 hover:text-gray-700 px-3 py-2 rounded-md text-sm font-medium transition-colors"
                  >
                    Выйти
                  </button>
                </div>
              </div>

              {/* Mobile User Info and Menu Button */}
              <div className="flex md:hidden items-center space-x-3">
                <div className="text-sm text-right">
                  <div className="font-medium text-gray-900">{user?.username}</div>
                  <div className="text-gray-500 text-xs">{getRoleDisplayName(user?.role || 'guest')}</div>
                </div>

                <button
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  className="p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors"
                  aria-label="Открыть меню"
                >
                  {mobileMenuOpen ? (
                    <XMarkIcon className="h-6 w-6" />
                  ) : (
                    <Bars3Icon className="h-6 w-6" />
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-b border-gray-200 shadow-lg">
          <div className="px-4 py-4 space-y-3">
            {/* Кнопка синхронизации только для админов */}
            {hasRole(['admin']) && (
              <button
                onClick={() => {
                  setShowSyncManagement(true);
                  setMobileMenuOpen(false);
                }}
                className="w-full text-left bg-green-600 hover:bg-green-700 text-white px-4 py-3 rounded-lg text-sm font-medium transition-colors flex items-center justify-between"
              >
                <span>Настройки синхронизации</span>
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.5 6h9.75M10.5 6a1.5 1.5 0 11-3 0m3 0a1.5 1.5 0 10-3 0M3.75 6H7.5m0 0L9 17.5M7.5 6v11.25m0 0h2.25m-2.25 0A2.25 2.25 0 007.5 19.5v0z" />
                </svg>
              </button>
            )}

            {/* Кнопки управления для админов и модераторов */}
            {hasRole(['admin', 'moderator']) && (
              <>
                <button
                  onClick={() => {
                    setShowUserManagement(true);
                    setMobileMenuOpen(false);
                  }}
                  className="w-full text-left bg-gray-600 hover:bg-gray-700 text-white px-4 py-3 rounded-lg text-sm font-medium transition-colors flex items-center justify-between"
                >
                  <span>Управление пользователями</span>
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </button>
                <button
                  onClick={() => {
                    setShowCreateUser(true);
                    setMobileMenuOpen(false);
                  }}
                  className="w-full text-left bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-3 rounded-lg text-sm font-medium transition-colors flex items-center justify-between"
                >
                  <span>Создать пользователя</span>
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                  </svg>
                </button>
              </>
            )}

            {/* Кнопка выхода */}
            <button
              onClick={() => {
                logout();
                setMobileMenuOpen(false);
              }}
              className="w-full text-left bg-red-50 hover:bg-red-100 text-red-700 px-4 py-3 rounded-lg text-sm font-medium transition-colors flex items-center justify-between border border-red-200"
            >
              <span>Выйти</span>
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
            </button>
          </div>
        </div>
      )}

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {children}
      </main>

      {/* Модальное окно создания пользователя */}
      {showCreateUser && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border max-w-5xl shadow-lg rounded-md bg-white max-h-[90vh] overflow-y-auto">
            <CreateUserForm
              onSuccess={() => {
                setShowCreateUser(false);
                // Можно добавить уведомление об успехе
              }}
              onCancel={() => setShowCreateUser(false)}
            />
          </div>
        </div>
      )}

      {/* Модальное окно управления пользователями */}
      {showUserManagement && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-4 sm:top-10 mx-2 sm:mx-auto p-2 sm:p-5 border max-w-5xl shadow-lg rounded-lg sm:rounded-md bg-white max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
            <UserManagement
              onClose={() => setShowUserManagement(false)}
            />
          </div>
        </div>
      )}

      {/* Модальное окно управления синхронизацией */}
      {showSyncManagement && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-5 mx-auto p-5 border max-w-5xl shadow-lg rounded-md bg-white max-h-[90vh] overflow-y-auto">
            <SyncManagement onClose={() => setShowSyncManagement(false)} />
          </div>
        </div>
      )}
    </div>
  );
};

export default Layout;